import { observer } from 'mobx-react-lite'
import React, { useContext, useMemo } from 'react'
import { CampaignContext } from '../../../../../../contexts/CampaignContext'
import Input from '../../../../../FormComponents/Input'
import FormulaItem from './FormulaParts/FormulaItem'
import CurrencyField from '../../../../../FormComponents/CurrencyField'
import { separateThousands, currencyFormat, currencyUnit } from '../../../../../../utils/Helper'
export default observer(function Formulas({ values, handleChange, handleBlur, setFieldValue }) {
  const { campaign, selectedScenario } = useContext(CampaignContext)

  const totalInteractions = useMemo(() => { return selectedScenario?.actions?.reduce((result, action) => { return result + (action?.action_insight?.clicks || 0) + (action?.action_insight?.comments || 0) + (action?.action_insight?.likes || 0) + (action?.action_insight?.saves || 0) + (action?.action_insight?.shares || 0) + (action?.action_insight?.views || 0) }, 0) }, [selectedScenario, selectedScenario?.actions, selectedScenario?.actions?.length])
  const CampaignEngagementRate = useMemo(() => { return (totalInteractions / values.gross_reach) * 100 }, [totalInteractions, values.gross_reach])
  const campaignClicks = useMemo(() => { return selectedScenario?.actions?.reduce((result, action) => { return result + (action?.action_insight?.clicks || 0) }, 0) }, [selectedScenario, selectedScenario?.actions, selectedScenario?.actions?.length])
  const campaignImpressions = useMemo(() => { return selectedScenario?.actions?.reduce((result, action) => { return result + (action?.action_insight?.impressions || action?.action_insight?.story_first_sequence_impressions || 0) }, 0)}, [selectedScenario, selectedScenario?.actions, selectedScenario?.actions?.length])
  const ClickThroughRate = useMemo(() => { return (campaignClicks / campaignImpressions) * 100 }, [campaignClicks, campaignImpressions])
  const totalSpent = useMemo(() => { return selectedScenario?.actions?.reduce((result, action) => { return result + (action?.advertiserPrice || 0) }, 0) }, [selectedScenario, selectedScenario?.actions, selectedScenario?.actions?.length])
  const CPM = useMemo(() => { return (totalSpent / campaignImpressions) * 1000 }, [totalSpent, campaignImpressions])
  const CPE = useMemo(() => { return totalSpent / totalInteractions }, [totalSpent, totalInteractions])
  
  const roi = useMemo(() => {
    if(values.revenue_generated && totalSpent) return (values.revenue_generated / totalSpent * 100).toFixed(2)
  }, [values.revenue_generated, totalSpent])

  return (
    <div>
      <FormulaItem
        title='Campaign Gross Reach'
        value={separateThousands(values.gross_reach)}
        formulaDescription='Gross Reach = Influencer 1 (Follower count * Actions) + Influencer 2(Follower count * Actions) + … '
        blockDescription='Gross Reach measures the total number of followers of an influencer multiplied by the amount of actions done by the influencers. It is a key metric to assess the overall exposure of the campaign. '
        imageClass='h-16 my-8'
        formulaImgSrc='/static/formulas/gross_reach.png'
      />
      <FormulaItem
        title='Total Interactions'
        value={separateThousands(totalInteractions)}
        formulaDescription='The formula must be weighted according to the reach and interactions of the individual influencers.'
        blockDescription='All interactions from all influencers and all actions within the campaign summed up to the total interactions'
        imageClass='h-12 my-8'
        formulaImgSrc='/static/formulas/interactions.png'
      />
      <FormulaItem
        title='Campaign engagement rate'
        value={`${CampaignEngagementRate.toFixed(2)} %`}
        blockDescription='The Engagement Rate measures how engaging the content is. It is calculated by dividing total interactions (likes, shares, comments) by the total reach of the content, then multiplying by 100 to express it as a percentage.'
        imageClass='h-16 my-8'
        formulaImgSrc='/static/formulas/engagement_rate.png'
      />
      <FormulaItem
        title='Click-Through Rate (CTR)'
        value={`${ClickThroughRate.toFixed(2)} %`}
        blockDescription='CTR measures the effectiveness of a campaign in encouraging users to act. It shows the percentage of impressions that resulted in clicks to a website, product page, or link.'
        imageClass='h-16 my-8'
        formulaImgSrc='/static/formulas/ctr.png'
      />
      <FormulaItem
        title='Cost per Thousand Impressions (CPM)'
        value={currencyFormat(CPM.toFixed(2), campaign.currency)}
        blockDescription='CPM calculates the cost to reach 1,000 unique users (impressions). It is a critical metric for understanding the cost-efficiency of the campaign.'
        imageClass='h-16 my-8'
        formulaImgSrc='/static/formulas/cpm.png'
      />
      <FormulaItem
        title='Cost per Engagement (CPE)'
        value={currencyFormat(CPE.toFixed(2), campaign.currency)} 
        blockDescription='CPE measures how much was spent on each engagement (like, comment, share). This helps determine the cost-effectiveness of generating user interactions.'
        imageClass='h-16 my-8'
        formulaImgSrc='/static/formulas/cpe.png'
      />
      <FormulaItem
        title='Conversion Rate (CR)'
        value={((values.conversions / campaignClicks) * 100).toFixed(2)}
        blockDescription="The Conversion Rate indicates the percentage of users who take the desired action (such as purchasing a product) after clicking on the campaign’s link. This is a crucial metric for evaluating the campaign's effectiveness in driving sales or other desired actions."
        imageClass='h-16 my-8'
        formulaImgSrc='/static/formulas/cr.png'
        visibleField='conversion_rate_visible'
        visibleValue={values.conversion_rate_visible}
        setFieldValue={setFieldValue}
      >
        <div className="w-1/2">
          <Input
            topMargin='my-5'
            label='Conversions'
            name='conversions'
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.conversions}
          />
        </div>
      </FormulaItem>
      <FormulaItem
        value={currencyFormat(roi, campaign.currency)}
        title='Return on Investment (ROI)'
        blockDescription='ROI measures the financial return of the campaign in comparison to the cost. It helps determine whether the campaign is financially successful.'
        imageClass='h-16 my-8'
        formulaImgSrc='/static/formulas/roi.png'
        visibleField='roi_visible'
        visibleValue={values.roi_visible}
        setFieldValue={setFieldValue}
      >
        <div className='flex gap-4 w-1/2'>
          <CurrencyField
            topMargin='mt-5 w-full'
            addon={currencyUnit(campaign.currency)}
            label='Revenue generated'
            name='revenue_generated'
            thousandSeparator='.'
            decimalSeparator=','
            decimalScale={2}
            fixedDecimalScale={true}
            valueIsNumericString={true}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.revenue_generated}
          />
        </div>
      </FormulaItem>
    </div>
  )
})